/**
 * EcosystemJoinForm: edit form for the `ecosystem-join` block.
 *
 * data: { heading, body, buttons: [{ text, link, variant }] }
 *
 * Heading input, body textarea, and a repeatable button list editor (1–4
 * buttons). Each button row has text, link, and variant select inputs.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Form, Row, Col, Button } from 'react-bootstrap';

import { CTA_BUTTON_VARIANTS } from '../blockTypes';

const MAX_BUTTONS = 4;

export default function EcosystemJoinForm({ data, onChange }) {
    const d = data || {};
    const buttons = Array.isArray(d.buttons) ? d.buttons : [];

    const update = (field, value) => onChange({ ...d, [field]: value });

    const updateButton = (index, field, value) => {
        const next = buttons.map((btn, i) =>
            i === index ? { ...btn, [field]: value } : btn
        );
        onChange({ ...d, buttons: next });
    };

    const addButton = () => {
        if (buttons.length >= MAX_BUTTONS) return;
        onChange({
            ...d,
            buttons: [...buttons, { text: '', link: '', variant: 'accent' }],
        });
    };

    const removeButton = (index) => {
        onChange({
            ...d,
            buttons: buttons.filter((_, i) => i !== index),
        });
    };

    return (
        <>
            <Form.Group className="mb-3" controlId="ecosystem-join-heading">
                <Form.Label>Heading</Form.Label>
                <Form.Control
                    type="text"
                    value={d.heading || ''}
                    onChange={(e) => update('heading', e.target.value)}
                    placeholder="Get Involved"
                />
            </Form.Group>

            <Form.Group className="mb-4" controlId="ecosystem-join-body">
                <Form.Label>Body</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    value={d.body || ''}
                    onChange={(e) => update('body', e.target.value)}
                    placeholder="Describe the call to action…"
                />
            </Form.Group>

            <Form.Label>Buttons</Form.Label>
            {buttons.map((btn, index) => (
                <div key={index} className="border rounded p-3 mb-3">
                    <Row className="g-2 align-items-end">
                        <Col md={4}>
                            <Form.Group
                                controlId={`ecosystem-join-btn-text-${index}`}
                            >
                                <Form.Label>Text</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={btn.text || ''}
                                    onChange={(e) =>
                                        updateButton(
                                            index,
                                            'text',
                                            e.target.value
                                        )
                                    }
                                    placeholder="Create an Account"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group
                                controlId={`ecosystem-join-btn-link-${index}`}
                            >
                                <Form.Label>Link</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={btn.link || ''}
                                    onChange={(e) =>
                                        updateButton(
                                            index,
                                            'link',
                                            e.target.value
                                        )
                                    }
                                    placeholder="/signup or https://…"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group
                                controlId={`ecosystem-join-btn-variant-${index}`}
                            >
                                <Form.Label>Style</Form.Label>
                                <Form.Select
                                    value={btn.variant || 'outline-light'}
                                    onChange={(e) =>
                                        updateButton(
                                            index,
                                            'variant',
                                            e.target.value
                                        )
                                    }
                                >
                                    {CTA_BUTTON_VARIANTS.map((v) => (
                                        <option key={v} value={v}>
                                            {v}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={1} className="d-flex align-items-end pb-1">
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removeButton(index)}
                                aria-label={`Remove button ${index + 1}`}
                            >
                                ✕
                            </Button>
                        </Col>
                    </Row>
                </div>
            ))}

            <Button
                variant="outline-secondary"
                size="sm"
                onClick={addButton}
                disabled={buttons.length >= MAX_BUTTONS}
            >
                + Add Button
            </Button>
            {buttons.length >= MAX_BUTTONS && (
                <Form.Text muted className="d-block mt-1">
                    Maximum of {MAX_BUTTONS} buttons reached.
                </Form.Text>
            )}
        </>
    );
}

EcosystemJoinForm.propTypes = {
    data: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
};
